import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.15 } },
  },
  item: {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0 },
  },
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data || []);
      } catch (error) {
        console.error("Failed to fetch posts:", error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Small delay to ensure backend is ready
    const timer = setTimeout(fetchPosts, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="pt-24" data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-16 bg-[#0D3B2E]" data-testid="blog-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="caption-text text-[#C5A059] mb-4 block">
              Blog
            </span>
            <h1 className="heading-xl text-[#F9F7F2]">Kahve Kültürü</h1>
            <p className="body-text text-[#F9F7F2]/70 mt-4 max-w-xl">
              Türk kahvesi hakkında bilmeniz gereken her şey: tarifler, tarih ve
              püf noktaları.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="blog-grid">
        <div className="section-container">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <motion.div
              variants={stagger.container}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {posts.map((post) => (
                <motion.article
                  key={post.id}
                  variants={stagger.item}
                  className="blog-card group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="relative overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#C5A059] text-[#0D3B2E] px-3 py-1 text-xs uppercase tracking-wider">
                          {post.category}
                        </span>
                      </div>
                    </div>
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4 text-[#5D4037]/60 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.read_time} dk okuma
                      </span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <h2 className="heading-md text-[#0D3B2E] mb-3 group-hover:text-[#C5A059] transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="body-text text-[#5D4037] text-sm line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#0D3B2E] hover:text-[#C5A059] transition-colors caption-text"
                      data-testid={`read-more-${post.slug}`}
                    >
                      Devamını Oku
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}

          {!loading && posts.length === 0 && (
            <div className="text-center py-20">
              <p className="body-text text-[#5D4037]">
                Henüz blog yazısı bulunmuyor.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
